import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FaGavel } from 'react-icons/fa';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import {
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
} from '../../util/fieldHelpers';
import { richText } from '../../util/richText';
import {
  formatExpiryCountdown,
  getExpiryTooltip,
  isListingExpired,
} from '../../util/listingExpiry';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
} from '../../util/types';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const REQUEST_GRID_VARIANT = 'requestGrid';
const REQUEST_ICON_COUNT = 4;
const QUANTITY_FIELD_PATTERN = /\b(qty|quantity|quantities|required|requested|needed|amount)\b/i;
const UNIT_FIELD_PATTERN = /\b(unit|uom|measure|package|packaging|container|bottle|bottles|vial|vials|box|boxes|case|cases|pack|packs|piece|pieces)\b/i;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

const getFirstDefinedValue = (data, keys) => {
  return keys.reduce((selected, key) => {
    return selected != null && selected !== '' ? selected : data?.[key];
  }, null);
};

const humanizeUnit = value => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  return value.replace(/[-_]/g, ' ');
};

const getFieldConfigText = config => {
  return [
    config?.key,
    config?.label,
    config?.showConfig?.label,
    config?.saveConfig?.label,
    config?.filterConfig?.label,
  ]
    .filter(Boolean)
    .join(' ');
};

const normalizeFieldConfigText = value => {
  return `${value || ''}`.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ');
};

const getFieldValueFromListingData = (publicData, metadata, config) => {
  const { key, scope = 'public' } = config;
  const source = scope === 'metadata' ? metadata : publicData;
  const value = source?.[key];

  return value != null && value !== '' ? value : null;
};

const getFieldOptionLabel = (value, enumOptions = []) => {
  const optionConfig = enumOptions.find(o => `${o.option}` === `${value}`);
  return optionConfig?.label || value;
};

const formatListingFieldValue = (value, config) => {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return null;
  }

  const { schemaType, enumOptions } = config;

  return schemaType === SCHEMA_TYPE_ENUM
    ? `${getFieldOptionLabel(value, enumOptions)}`
    : schemaType === SCHEMA_TYPE_MULTI_ENUM
    ? value.map(v => getFieldOptionLabel(v, enumOptions)).join(', ')
    : schemaType === SCHEMA_TYPE_LONG || schemaType === SCHEMA_TYPE_TEXT
    ? `${value}`
    : null;
};

const getListingFieldValueLabel = (publicData, metadata, config) => {
  const value = getFieldValueFromListingData(publicData, metadata, config);
  return formatListingFieldValue(value, config);
};

const getSelectedListingCategories = (publicData = {}, categoryConfiguration = {}) => {
  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;

  if (!categoryPrefix || !listingCategoriesConfig) {
    return [];
  }

  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  return Object.values(categoriesObj);
};

const isListingFieldForCard = (config, publicData, selectedCategories) => {
  const listingType = publicData?.listingType;
  const isTargetListingType = isFieldForListingType(listingType, config);
  const isTargetCategory = isFieldForCategory(selectedCategories, config);

  return isTargetListingType && isTargetCategory;
};

const getConfiguredQuantityLabel = (
  publicData,
  metadata,
  listingFieldConfigs,
  categoryConfiguration
) => {
  const selectedCategories = getSelectedListingCategories(publicData, categoryConfiguration);
  const availableFieldConfigs = (listingFieldConfigs || []).filter(config => {
    const value = getFieldValueFromListingData(publicData, metadata, config);
    return value != null && isListingFieldForCard(config, publicData, selectedCategories);
  });
  const quantityFieldConfig = availableFieldConfigs.find(config =>
    QUANTITY_FIELD_PATTERN.test(normalizeFieldConfigText(getFieldConfigText(config)))
  );

  if (!quantityFieldConfig) {
    return null;
  }

  const quantityLabel = getListingFieldValueLabel(publicData, metadata, quantityFieldConfig);
  const quantityFieldText = normalizeFieldConfigText(getFieldConfigText(quantityFieldConfig));
  const unitFieldConfig = availableFieldConfigs.find(config => {
    const fieldText = normalizeFieldConfigText(getFieldConfigText(config));
    return config.key !== quantityFieldConfig.key && UNIT_FIELD_PATTERN.test(fieldText);
  });
  const unitLabel = unitFieldConfig
    ? getListingFieldValueLabel(publicData, metadata, unitFieldConfig)
    : null;

  return formatQuantityLabel(quantityLabel, unitLabel, quantityFieldText);
};

const formatQuantityLabel = (quantity, unit, sourceText = '') => {
  const quantityString = quantity != null && quantity !== '' ? `${quantity}`.trim() : null;
  const unitString = unit != null && unit !== '' ? `${unit}`.trim() : null;
  const quantityAlreadyHasUnit = unitString
    ? quantityString?.toLowerCase().includes(unitString.toLowerCase())
    : false;
  const sourceLooksUnitSpecific = UNIT_FIELD_PATTERN.test(sourceText);

  return quantityString && unitString && !quantityAlreadyHasUnit && !sourceLooksUnitSpecific
    ? `${quantityString} ${unitString}`
    : quantityString;
};

const getDirectQuantityLabel = (publicData = {}, metadata = {}) => {
  const listingData = { ...metadata, ...publicData };
  const quantity = getFirstDefinedValue(listingData, [
    'quantity',
    'qty',
    'requestedQuantity',
    'requestQuantity',
    'requiredQuantity',
    'neededQuantity',
    'quantityNeeded',
    'quantityRequired',
    'amount',
  ]);
  const unit = humanizeUnit(
    getFirstDefinedValue(listingData, [
      'quantityUnit',
      'requestedQuantityUnit',
      'requestQuantityUnit',
      'unit',
      'unitLabel',
      'uom',
      'package',
      'packaging',
      'container',
      'containerType',
    ])
  );

  return formatQuantityLabel(quantity, unit);
};

const getQuantityLabel = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, stockQuantity } = props;
  const configuredQuantityLabel = getConfiguredQuantityLabel(
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration
  );
  const directQuantityLabel = getDirectQuantityLabel(publicData, metadata);

  return configuredQuantityLabel || directQuantityLabel || formatQuantityLabel(stockQuantity);
};

const getPostedAtLabel = createdAt => {
  if (!createdAt) {
    return null;
  }

  const createdDate = new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) {
    return null;
  }

  const diffMs = Date.now() - createdDate.getTime();
  const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  if (days === 0) {
    return 'Today';
  } else if (days === 1) {
    return '1 day ago';
  } else if (days < 30) {
    return `${days} days ago`;
  }

  return createdDate.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getStableRequestIconIndex = value => {
  const seed = `${value || ''}`;
  const hash = seed.split('').reduce((sum, character) => {
    return (sum * 31 + character.charCodeAt(0)) >>> 0;
  }, 0);

  return hash % REQUEST_ICON_COUNT;
};

const RequestVisualIcon = props => {
  const { iconIndex } = props;

  switch (iconIndex) {
    case 1:
      return (
        <svg
          className={css.requestIconSvg}
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.25 10.9667L8.80835 4.94666"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M24.5 18.6667V9.33333C24.4991 8.50057 24.0545 7.73138 23.3333 7.315L15.1667 2.64833C14.4447 2.23152 13.5553 2.23152 12.8333 2.64833L4.66667 7.315C3.94547 7.73138 3.50085 8.50057 3.5 9.33333V18.6667C3.50085 19.4994 3.94547 20.2686 4.66667 20.685L12.8333 25.3517C13.5553 25.7685 14.4447 25.7685 15.1667 25.3517L23.3333 20.685C24.0545 20.2686 24.4991 19.4994 24.5 18.6667"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M3.83832 8.16666L14 14L24.1616 8.16666"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 25.6667V14"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 2:
      return (
        <svg
          className={css.requestIconSvg}
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.6667 2.33334V10.8617L6.67333 21C6.34296 21.7242 6.40461 22.5669 6.83686 23.2354C7.26911 23.9038 8.01231 24.3058 8.80833 24.3017H19.1917C19.9877 24.3058 20.7309 23.9038 21.1631 23.2354C21.5954 22.5669 21.657 21.7242 21.3267 21L16.3333 10.8617V2.33334"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.91663 2.33334H18.0833"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 3:
      return (
        <svg
          className={css.requestIconSvg}
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.5 3.5H4.66667V8.16667H3.5V3.5"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.16666 3.5H9.33332V8.16667H8.16666V3.5"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.8333 3.5H14V8.16667H12.8333V3.5"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.5 10.5H17.5C18.7878 10.5 19.8333 11.5455 19.8333 12.8333V23.3333C19.8333 24.6211 18.7878 25.6667 17.5 25.6667H10.5C9.21219 25.6667 8.16666 24.6211 8.16666 23.3333V12.8333C8.16666 11.5455 9.21219 10.5 10.5 10.5"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 0:
    default:
      return (
        <svg
          className={css.requestIconSvg}
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.25 23.9167L23.9167 12.25C25.4002 10.7962 25.9886 8.65766 25.4578 6.64953C24.927 4.6414 23.3586 3.07303 21.3505 2.5422C19.3423 2.01136 17.2038 2.59983 15.75 4.08333L4.08333 15.75C2.59983 17.2038 2.01136 19.3423 2.5422 21.3505C3.07303 23.3586 4.6414 24.927 6.64953 25.4578C8.65766 25.9886 10.7962 25.4002 12.25 23.9167"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.91666 9.91666L18.0833 18.0833"
            fill="none"
            stroke="#A8C8E8"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
  }
};

export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    variant,
  } = props;

  const isRequestGrid = variant === REQUEST_GRID_VARIANT;

  const classes = classNames(rootClassName || css.root, className, {
    [css.requestGridRoot]: isRequestGrid,
  });

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const {
    title = '',
    description = '',
    price,
    publicData,
    metadata,
    createdAt,
  } = currentListing.attributes;
  const requestDescription =
    typeof description === 'string' && description.trim().length > 0 ? description.trim() : null;
  const slug = createSlug(title);
  const expiresAt = publicData?.expiresAt;
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!expiresAt) {
      return undefined;
    }

    const timer = setInterval(() => {
      setNow(Date.now());
    }, 60 * 1000);

    return () => {
      clearInterval(timer);
    };
  }, [expiresAt]);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName || 'Marketplace member';

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const hasCurrentUserBid =
    props.hasCurrentUserBid === true ||
    metadata?.hasCurrentUserBid === true ||
    metadata?.hasProviderSubmittedOffer === true;
  const quantityLabel = getQuantityLabel({
    publicData,
    metadata,
    listingFieldConfigs: config.listing.listingFields,
    categoryConfiguration: config.categoryConfiguration,
    stockQuantity: currentListing.currentStock?.attributes?.quantity,
  });
  const requestBadgeLabel = hasCurrentUserBid ? 'Bid already placed' : 'Describe what you need';
  const postedAtLabel = getPostedAtLabel(createdAt);
  const expiryCountdown = formatExpiryCountdown(expiresAt, now);
  const expiryTooltip = getExpiryTooltip(expiresAt);
  const isExpired = isListingExpired(currentListing, now);
  const submitOfferDisabled = hasCurrentUserBid || isExpired;
  const submitOfferTitle = isExpired
    ? 'This request has expired.'
    : 'You have already submitted an offer.';
  const submitOfferLabel = isExpired ? 'Expired' : 'Offer Submitted';
  const requestIconIndex = getStableRequestIconIndex(`${id}-${title}`);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return isRequestGrid ? (
    <div className={classes} {...setActivePropsMaybe}>
      <NamedLink className={css.requestCardLink} name="ListingPage" params={{ id, slug }}>
        <div className={css.requestVisual}>
          <span
            className={classNames(css.requestBadge, {
              [css.requestBadgeSuccess]: hasCurrentUserBid,
            })}
          >
            {hasCurrentUserBid ? (
              <FaGavel className={css.requestBadgeBidIcon} aria-hidden="true" />
            ) : (
              <span className={css.requestBadgeDot} />
            )}
            {requestBadgeLabel}
          </span>
          <span className={css.requestIcon} aria-hidden="true">
            <RequestVisualIcon iconIndex={requestIconIndex} />
          </span>
        </div>

        <div className={css.requestInfo}>
          <h2 className={css.requestTitle}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </h2>

          {requestDescription ? (
            <p className={css.requestDescription}>
              {richText(requestDescription, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </p>
          ) : null}

          {quantityLabel ? (
            <p className={css.requestQuantity}>
              <FormattedMessage
                id="ListingCard.quantityLabel"
                values={{ quantity: quantityLabel }}
              />
            </p>
          ) : null}

          <div className={css.requestMeta}>
            {showAuthorInfo ? (
              <>
                <span className={css.metaIcon} aria-hidden="true">
                  A
                </span>
                <span className={css.metaText}>{authorName}</span>
              </>
            ) : null}
            {showAuthorInfo && postedAtLabel ? <span className={css.metaDivider}>.</span> : null}
            {postedAtLabel ? <span className={css.metaText}>{postedAtLabel}</span> : null}
            {(showAuthorInfo || postedAtLabel) && expiryCountdown ? (
              <span className={css.metaDivider}>.</span>
            ) : null}
            {expiryCountdown ? (
              <span
                className={classNames(css.metaText, css.expiryMeta, {
                  [css.expiryMetaExpired]: isExpired,
                })}
                title={expiryTooltip || undefined}
              >
                {expiryCountdown}
              </span>
            ) : null}
          </div>
          {isExpired ? <p className={css.requestExpiredNotice}>This request has expired.</p> : null}
        </div>
      </NamedLink>

      <div className={css.actions}>
        <NamedLink
          name="ListingPage"
          params={{ id, slug }}
          className={css.viewListingBtn}
          onClick={e => e.stopPropagation()}
        >
          View listing
        </NamedLink>

        {submitOfferDisabled ? (
          <span
            className={classNames(css.submitOfferBtn, css.submitOfferBtnDisabled)}
            title={submitOfferTitle}
            aria-disabled="true"
          >
            {submitOfferLabel}
          </span>
        ) : (
          <NamedLink
            name="MakeOfferPage"
            params={{ id, slug }}
            className={css.submitOfferBtn}
            onClick={e => e.stopPropagation()}
          >
            Submit Offer
          </NamedLink>
        )}
      </div>
    </div>
  ) : (
    <article className={classes}>
      <div className={css.cardMedia}>
        <NamedLink className={css.imageLink} name="ListingPage" params={{ id, slug }}>
          <ListingCardImage
            renderSizes={renderSizes}
            title={title}
            currentListing={currentListing}
            config={config}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
            style={cardStyle}
            showListingImage={showListingImage}
          />
        </NamedLink>
      </div>

      <NamedLink className={css.infoLink} name="ListingPage" params={{ id, slug }}>
        <div className={css.info}>
          <PriceMaybe
            price={price}
            publicData={publicData}
            config={config}
            intl={intl}
            listingTypeConfig={foundListingTypeConfig}
          />

          <div className={css.mainInfo}>
            {showListingImage && (
              <div className={css.title}>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
              </div>
            )}

            {showAuthorInfo ? (
              <div className={css.authorInfo}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              </div>
            ) : null}
          </div>
        </div>
      </NamedLink>
    </article>
  );
};

export default ListingCard;
